import Phaser from "phaser";
import type { AnimeFX } from "./AnimeFX";

/**
 * Adds the "feel-good" layer: floating damage numbers, kill explosions,
 * camera shake, hit-stop, combo counter. This is what makes 15-second
 * gameplay clips watchable on TikTok.
 */
export class JuiceSystem {
  private scene: Phaser.Scene;
  private comboCount = 0;
  private comboLastKillAt = 0;
  private comboText?: Phaser.GameObjects.Text;
  private comboTimer?: Phaser.Time.TimerEvent;
  private anime?: AnimeFX;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Wire the anime-spectacle layer (set right after construction). */
  setAnime(anime: AnimeFX): void {
    this.anime = anime;
  }

  /** Call when a tower projectile lands a hit. */
  onHit(x: number, y: number, damage: number, color: number, isCrit = false): void {
    this.spawnDamageNumber(x, y, Math.round(damage), color, isCrit);
    if (isCrit) {
      // Big crits get the full anime impact frame (flash + speed lines + star
      // + hit-stop + zoom punch). Small crits stay snappy with a gold ring.
      if (damage >= 50 && this.anime) {
        const intensity = Math.min(1, damage / 140);
        this.anime.impact(x, y, 0xffe066, intensity);
      } else {
        const ring = this.scene.add.circle(x, y, 6, 0xffe066, 0).setStrokeStyle(2, 0xffe066, 0.9).setDepth(99);
        this.scene.tweens.add({
          targets: ring,
          scale: { from: 0.5, to: 2.4 },
          alpha: { from: 0.9, to: 0 },
          duration: 260,
          ease: "Cubic.easeOut",
          onComplete: () => ring.destroy(),
        });
        this.anime?.speedLines(x, y, 0xffe066, 8, 90);
        this.scene.cameras.main.shake(50, 0.002);
      }
    }
  }

  /** Call when an enemy dies. Awards combo, plays explosion, mild shake. */
  onKill(x: number, y: number, color: number): void {
    const now = this.scene.time.now;
    if (now - this.comboLastKillAt < 1000) {
      this.comboCount++;
    } else {
      this.comboCount = 1;
    }
    this.comboLastKillAt = now;

    this.spawnExplosion(x, y, color);
    // Only shake on combo milestones (every 3rd kill in a chain) so a busy screen
    // doesn't shake constantly. Shake magnitude is much smaller now.
    if (this.comboCount >= 3) {
      if (this.comboCount === 3 || this.comboCount % 3 === 0) {
        this.scene.cameras.main.shake(60, 0.0015 + Math.min(this.comboCount, 10) * 0.00025);
      }
      this.showCombo(this.comboCount);
    }
    // No per-kill shake — single kills are noise, not an event.
  }

  /** Call when a leak happens — angry red flash + heavy shake. */
  onLeak(): void {
    this.scene.cameras.main.shake(220, 0.012);
    this.scene.cameras.main.flash(180, 255, 60, 60, true);
  }

  /** Call when a wave starts — small whoosh shake. */
  onWaveStart(): void {
    this.scene.cameras.main.shake(80, 0.003);
  }

  /** Call when a wave is cleared — green flash. */
  onWaveClear(): void {
    this.scene.cameras.main.flash(220, 80, 220, 120, true);
  }

  // -----------------------------------------------------------------

  private spawnDamageNumber(x: number, y: number, dmg: number, color: number, isCrit = false): void {
    if (isCrit) {
      // Crits are an event: big gold text with a "!" and a satisfying pop.
      const txt = this.scene.add.text(x, y, `${dmg}!`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "26px",
        fontStyle: "bold",
        color: "#ffe066",
        stroke: "#7a3a00",
        strokeThickness: 5,
      });
      txt.setOrigin(0.5);
      txt.setDepth(101);
      txt.setShadow(0, 3, "#000", 5, false, true);
      this.scene.tweens.add({
        targets: txt,
        y: y - 42,
        scale: { from: 1.8, to: 1.1 },
        alpha: { from: 1, to: 0 },
        duration: 850,
        ease: "Cubic.easeOut",
        onComplete: () => txt.destroy(),
      });
      return;
    }

    // Normal hits: size + brightness scale with magnitude so heavy hits read big.
    const big = dmg >= 40;
    const mid = dmg >= 18;
    const txt = this.scene.add.text(x, y, String(dmg), {
      fontFamily: "system-ui, sans-serif",
      fontSize: big ? "20px" : mid ? "15px" : "12px",
      fontStyle: "bold",
      color: this.colorToHex(color),
      stroke: "#000",
      strokeThickness: 3,
    });
    txt.setOrigin(0.5);
    txt.setDepth(100);

    const driftX = Phaser.Math.Between(-12, 12);
    this.scene.tweens.add({
      targets: txt,
      y: y - 28,
      x: x + driftX,
      alpha: { from: 1, to: 0 },
      scale: { from: big ? 1.3 : 1, to: 1.3 },
      duration: 700,
      ease: "Cubic.easeOut",
      onComplete: () => txt.destroy(),
    });
  }

  private spawnExplosion(x: number, y: number, color: number): void {
    // Burst of round particles flung outward.
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 12 + Math.random() * 18;
      const p = this.scene.add.circle(x, y, 3 + Math.random() * 2, color);
      this.scene.tweens.add({
        targets: p,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: { from: 1, to: 0 },
        scale: { from: 1, to: 0.2 },
        duration: 400 + Math.random() * 200,
        ease: "Quad.easeOut",
        onComplete: () => p.destroy(),
      });
    }
    // Gibs: small square shards that tumble + arc with gravity for a chunkier death.
    for (let i = 0; i < 5; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 16 + Math.random() * 22;
      const s = 2 + Math.random() * 3;
      const shard = this.scene.add.rectangle(x, y, s, s, color);
      shard.setDepth(99);
      const tx = x + Math.cos(angle) * dist;
      const ty = y + Math.sin(angle) * dist;
      this.scene.tweens.add({
        targets: shard,
        x: tx,
        y: ty + 18, // gravity arc
        angle: Phaser.Math.Between(-220, 220),
        alpha: { from: 1, to: 0 },
        duration: 480 + Math.random() * 220,
        ease: "Quad.easeIn",
        onComplete: () => shard.destroy(),
      });
    }
    // Bright shockwave ring.
    const ring = this.scene.add.circle(x, y, 6, 0xffffff, 0).setStrokeStyle(2, 0xffffff, 0.8).setDepth(98);
    this.scene.tweens.add({
      targets: ring,
      scale: { from: 0.6, to: 3 },
      alpha: { from: 0.7, to: 0 },
      duration: 300,
      ease: "Cubic.easeOut",
      onComplete: () => ring.destroy(),
    });
    // Center flash
    const flash = this.scene.add.circle(x, y, 14, 0xffffff, 0.7);
    this.scene.tweens.add({
      targets: flash,
      scale: { from: 0.4, to: 1.6 },
      alpha: { from: 0.9, to: 0 },
      duration: 220,
      ease: "Cubic.easeOut",
      onComplete: () => flash.destroy(),
    });
  }

  private showCombo(count: number): void {
    const { width, height } = this.scene.scale;
    if (this.comboText) this.comboText.destroy();
    this.comboTimer?.remove();

    const c = this.scene.add
      .text(width / 2, height * 0.35, `x${count} COMBO!`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: count >= 6 ? "44px" : "32px",
        fontStyle: "bold",
        color: count >= 8 ? "#ff4d4d" : count >= 5 ? "#ff9b3a" : "#fff066",
        stroke: "#000",
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(200);
    c.setShadow(0, 4, "#000", 6, false, true);
    this.comboText = c;

    this.scene.tweens.add({
      targets: c,
      scale: { from: 1.4, to: 1 },
      duration: 200,
      ease: "Back.easeOut",
    });
    this.scene.tweens.add({
      targets: c,
      alpha: 0,
      y: height * 0.35 - 32,
      delay: 600,
      duration: 400,
      ease: "Cubic.easeIn",
      onComplete: () => {
        c.destroy();
        if (this.comboText === c) this.comboText = undefined;
      },
    });
  }

  private colorToHex(c: number): string {
    return "#" + c.toString(16).padStart(6, "0");
  }
}
